import { useEffect, useRef } from "react";
import { track } from "@/lib/analytics";
import type { LabAcquisitionOffer } from "@/lib/api/labAcquisitionHooks";
import type { UpsellQuoteItem } from "@/lib/api/upsellHooks";
import { UpsellCard } from "./UpsellCard";

export function labUpsellItem(offer: LabAcquisitionOffer): UpsellQuoteItem {
  return {
              sanity_product_id: offer.product_id,
              canonical_product_id: offer.product_id,
              slug: null,
              title: `Blood test · ${offer.title}`,
              description: "Collect at home, return your sample, and explore your lab results in your portal.",
              treatment_support: null,
              method: null,
              quantity: null,
              pricing_mode: null,
              monthly_price_cents: offer.amount_cents,
              monthly_compare_price_cents: null,
              monthly_renewal_price_cents: offer.renewal_amount_cents,
              quarterly_price_cents: null,
              quarterly_compare_price_cents: null,
              quarterly_renewal_price_cents: null,
              hero_image_url: "/images/tasso/system-collection-device-transparent.png",
              value_props: [
                `${offer.measurement_count} reported measurements`,
                "HbA1c · ApoB · Cholesterol · hs-CRP · Total testosterone",
                "Results in your portal + downloadable laboratory PDF",
              ],
            };
}

/** Selection only. All payment and consent live in the shared stack summary. */
export function LabUpsellCard({ offer, active = true, selected, onToggle }: {
  offer: LabAcquisitionOffer;
  active?: boolean;
  selected: boolean;
  onToggle: () => void;
}) {
  const viewed = useRef(false);
  const section = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!active || !section.current || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || viewed.current) return;
      viewed.current = true;
      track("blood_test_upsell_viewed", {
        experiment_key: offer.experiment_key, variant_key: offer.variant,
        product_id: offer.product_id, source: "post_purchase_upsell",
      });
    }, { threshold: 0.25 });
    observer.observe(section.current);
    return () => observer.disconnect();
  }, [active, offer]);
  return <section ref={section} aria-label="At-home blood test offer">
    <UpsellCard kind="lab" item={labUpsellItem(offer)} selected={selected}
      frequency="monthly" onFrequencyChange={() => undefined} onToggle={onToggle} />
  </section>;
}
